import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Instagram, Facebook } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LegalModal } from "./LegalModal";
import { HashLink } from "./HashLink";
import { LEGAL } from "../constants/legal";
import { SOCIALS } from "../constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TikTokIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export const Footer = () => {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [legal, setLegal] = useState(null); // "terms" | "privacy" | "accessibility"

  const links = [
    { to: "/", label: t.menu.home, tid: "footer-home-link" },
    { to: "/book", label: t.nav.reserve, tid: "footer-reserve-link" },
    { to: "/#experience", label: t.nav.experience, tid: "footer-experience-link" },
    { to: "/#studio", label: t.nav.studio, tid: "footer-studio-link" },
    { to: "/#pricing", label: t.nav.pricing, tid: "footer-pricing-link" },
    { to: "/#location", label: t.nav.location, tid: "footer-location-link" },
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setMsg({ ok: false, text: t.footer.invalidEmail });
      return;
    }
    try {
      await axios.post(`${API}/newsletter`, { email });
      setMsg({ ok: true, text: t.footer.subscribed });
      setEmail("");
    } catch {
      setMsg({ ok: false, text: t.footer.invalidEmail });
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#030303] px-6 md:px-14 pt-20 overflow-hidden" data-testid="footer">
      <div className="grid md:grid-cols-2 gap-14">
        <nav className="space-y-1">
          {links.map((l) => {
            const cls =
              "group block w-fit font-serif text-2xl md:text-3xl text-zinc-300 hover:text-[#C5A059] hover:italic transition-all duration-500";
            const isLanding = l.to === "/" || l.to.startsWith("/#");
            return isLanding ? (
              <HashLink key={l.tid} id={l.to === "/" ? "/" : l.to.slice(1)} data-testid={l.tid} className={cls}>
                {l.label}
              </HashLink>
            ) : (
              <Link key={l.tid} to={l.to} data-testid={l.tid} className={cls}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="md:justify-self-end md:text-right md:max-w-sm w-full">
          <p className="overline-gold">{t.footer.stay}</p>
          <form onSubmit={subscribe} noValidate className="mt-6 flex items-center border-b border-white/20 focus-within:border-[#C5A059] transition-colors duration-300">
            <input
              data-testid="newsletter-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.footer.emailPlaceholder}
              className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-zinc-600 outline-none md:text-right"
            />
            <button data-testid="newsletter-submit-btn" type="submit" aria-label="Subscribe" className="p-2 text-[#C5A059] hover:translate-x-1 transition-transform duration-300">
              <ArrowRight size={18} />
            </button>
          </form>
          {msg && (
            <p data-testid="newsletter-message" className={`mt-3 text-xs ${msg.ok ? "text-[#C5A059]" : "text-red-400"}`}>
              {msg.text}
            </p>
          )}
          <div className="mt-8 flex md:justify-end gap-6">
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-[#C5A059] transition-colors duration-300" data-testid="social-instagram">
              <Instagram size={14} /> Instagram
            </a>
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-[#C5A059] transition-colors duration-300" data-testid="social-facebook">
              <Facebook size={14} /> Facebook
            </a>
            <a href={SOCIALS.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-[#C5A059] transition-colors duration-300" data-testid="social-tiktok">
              <TikTokIcon size={13} /> TikTok
            </a>
          </div>
        </div>
      </div>

      <div className="mt-20 select-none pointer-events-none" aria-hidden="true">
        <p className="font-serif text-[19vw] leading-[0.78] text-white/90 whitespace-nowrap text-center translate-y-[0.08em]">
          APEX <em className="italic text-[#C5A059]/90">Ride</em>
        </p>
      </div>

      <div className="border-t border-white/5 py-6 flex flex-col md:flex-row flex-wrap items-center justify-between gap-3 text-[0.65rem] text-zinc-600">
        <p>
          © 2026 Apex Ride — {t.footer.rights} · {t.footer.madeBy}{" "}
          <a
            href="https://leyvawebstudio.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit-link"
            className="text-zinc-400 hover:text-[#C5A059] underline underline-offset-2 transition-colors duration-300"
          >
            leyvawebstudio.com
          </a>
        </p>
        <div className="flex flex-wrap gap-5">
          <button data-testid="legal-accessibility" onClick={() => setLegal("accessibility")} className="hover:text-[#C5A059] hover:underline underline-offset-2 transition-colors duration-300">
            {t.footer.accessibility}
          </button>
          <button data-testid="legal-terms" onClick={() => setLegal("terms")} className="hover:text-[#C5A059] hover:underline underline-offset-2 transition-colors duration-300">
            {t.footer.terms}
          </button>
          <button data-testid="legal-privacy" onClick={() => setLegal("privacy")} className="hover:text-[#C5A059] hover:underline underline-offset-2 transition-colors duration-300">
            {t.footer.privacy}
          </button>
        </div>
        <p>{t.footer.payments}</p>
      </div>

      <LegalModal doc={legal ? LEGAL[legal][lang] : null} onClose={() => setLegal(null)} />
    </footer>
  );
};
